import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const navigate = useNavigate();

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-14 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-4">该文章可能已被移除或链接失效</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const allArticles = articles.filter((a) => a.id !== article.id);
  const prevArticle = allArticles.length > 0 ? allArticles[0] : null;
  const nextArticle = allArticles.length > 1 ? allArticles[1] : null;

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: article.categoryName, to: `/category/${article.category}` },
    { label: article.title },
  ];

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    url: `https://naishuidonghua.cn/article/${article.slug}`,
    inLanguage: 'zh-Hans',
    author: {
      '@type': 'Organization',
      name: '奶水动画',
    },
    publisher: {
      '@type': 'Organization',
      name: '奶水动画',
      url: 'https://naishuidonghua.cn/',
    },
    image: article.image,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      <ScrollToTop />
      <Navbar />

      <main className="pt-24 pb-14 md:pb-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6">
            <span className="inline-block text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 mb-4 whitespace-nowrap">
              {article.categoryName}
            </span>

            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 leading-snug">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>{article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>{article.readTime} 分钟阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>2.3k 次阅读
              </span>
            </div>

            <img
              src={article.image}
              alt={article.title}
              className="w-full h-64 md:h-80 object-cover rounded-xl mb-8"
              title={article.title}
            />

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Prev / Next Navigation */}
            <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8 pt-6 border-t border-background-200/70">
              {prevArticle ? (
                <Link
                  to={`/article/${prevArticle.slug}`}
                  className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors group cursor-pointer"
                >
                  <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform"></i>
                  <span className="line-clamp-1">{prevArticle.title}</span>
                </Link>
              ) : (
                <div></div>
              )}
              {nextArticle ? (
                <Link
                  to={`/article/${nextArticle.slug}`}
                  className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors group cursor-pointer"
                >
                  <span className="line-clamp-1">{nextArticle.title}</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </Link>
              ) : (
                <div></div>
              )}
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-10 pt-8 border-t border-background-200/70">
                <h3 className="font-heading text-lg text-foreground-900 mb-4">相关文章</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-300 transition-all cursor-pointer"
                    >
                      <h4 className="font-heading text-sm text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug mb-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}